import { transport } from './client';
import {
  anyBody,
  arrayOf,
  bool,
  int,
  nullableStr,
  object,
  str,
  record,
  optional,
  literal,
  type Decoder,
} from './decode';

export const sourceDecoder = object({
  id: str,
  name: str,
  adapter_type: str,
  collection_mode: literal('fixed_tracking', 'keyword_search'),
  max_results_per_keyword: int,
  supports_topic_queries: bool,
  topic_queries_managed: bool,
  status: str,
  is_enabled: bool,
  last_crawl_at: nullableStr,
  last_success_at: nullableStr,
  review_status: str,
  success_requests: int,
  failed_requests: int,
  subscribed: bool,
});
const requestDecoder = object({
  id: str,
  name: str,
  adapter_type: str,
  status: str,
  reason: nullableStr,
  source_id: nullableStr,
  config: optional(record(anyBody), {}),
});
const progressDecoder = object({
  status: str,
  processed: int,
  matched: int,
  analysis_pending: int,
  analysis_failed: int,
  complete: bool,
});
const sentimentDecoder = object({ status: str, label: nullableStr });

export const fetchPublicSources = (signal?: AbortSignal) =>
  transport.request('/crawlers/public/sources', arrayOf(sourceDecoder), signal ? { signal } : {});
export const fetchSourceRequests = (operator: boolean, signal?: AbortSignal) =>
  transport.request(
    operator ? '/ops/public-sources/requests' : '/crawlers/public/requests',
    arrayOf(requestDecoder),
    signal ? { signal } : {},
  );
export const submitPublicSource = (input: {
  name: string;
  adapter_type: string;
  config: Record<string, unknown>;
  source_url?: string | null;
  collection_mode?: string;
  max_results_per_keyword?: number;
  use_proxy?: boolean;
  rate_limit_rpm?: number;
}) => transport.request('/ops/public-sources', anyBody, { method: 'POST', body: input });
export const subscribeSource = (id: string, enabled: boolean) =>
  transport.request(`/crawlers/public/sources/${encodeURIComponent(id)}/subscription`, anyBody, {
    method: 'PUT',
    body: { enabled },
  });
/**
 * The public trigger answers `{created, reused}` (see the generated schema), while
 * older deployments answered `{jobs_created}`. Both normalise to `jobs_created` so
 * callers keep one "was a new job queued?" check.
 */
export const decodePublicTrigger: Decoder<{ jobs_created: number; created: boolean }> = (
  value,
  path,
) => {
  const body = object({
    created: optional(bool, false),
    jobs_created: optional(int, 0),
  })(value, path);
  const created = body.created || body.jobs_created > 0;
  return { created, jobs_created: created ? Math.max(1, body.jobs_created) : 0 };
};
export const triggerPublicSource = (id: string) =>
  transport.request(
    `/crawlers/public/sources/${encodeURIComponent(id)}/trigger`,
    decodePublicTrigger,
    {
      method: 'POST',
    },
  );
export const reviewSource = (id: string, approve: boolean, reason: string) =>
  transport.request(
    `/ops/public-sources/requests/${encodeURIComponent(id)}/review`,
    requestDecoder,
    { method: 'POST', body: { decision: approve ? 'approved' : 'rejected', reason } },
  );
export const setPublicSourceState = (id: string, enabled: boolean) =>
  transport.request(`/ops/public-sources/${encodeURIComponent(id)}/state`, anyBody, {
    method: 'PATCH',
    body: { enabled },
  });
export const fetchBackfill = (topicId: string, signal?: AbortSignal) =>
  transport.request(
    `/topics/${encodeURIComponent(topicId)}/backfill`,
    progressDecoder,
    signal ? { signal } : {},
  );
export const restartBackfill = (topicId: string) =>
  transport.request(`/topics/${encodeURIComponent(topicId)}/backfill`, anyBody, { method: 'POST' });
export const analyzeContent = (id: string) =>
  transport.request(`/contents/${encodeURIComponent(id)}/sentiment`, anyBody, { method: 'POST' });
export const fetchSentiment = (id: string, signal?: AbortSignal) =>
  transport.request(
    `/contents/${encodeURIComponent(id)}/sentiment`,
    sentimentDecoder,
    signal ? { signal } : {},
  );
export const hidePublicContent = (id: string) =>
  transport.request(`/contents/${encodeURIComponent(id)}/hide`, anyBody, { method: 'POST' });

export const withdrawPublicContent = (id: string, reason: string) =>
  transport.request(`/ops/public-contents/${encodeURIComponent(id)}/withdraw`, anyBody, {
    method: 'POST',
    body: { reason },
  });
const sourceConfigDecoder = object({
  name: str,
  adapter_type: str,
  config: record(anyBody),
  source_url: nullableStr,
  collection_mode: literal('fixed_tracking', 'keyword_search'),
  max_results_per_keyword: int,
  use_proxy: bool,
  rate_limit_rpm: int,
});
export const fetchPublicSourceConfig = (id: string) =>
  transport.request(`/ops/public-sources/${encodeURIComponent(id)}`, sourceConfigDecoder);
export const updatePublicSource = (
  id: string,
  input: {
    name: string;
    adapter_type: string;
    config: Record<string, unknown>;
    source_url?: string | null;
    collection_mode?: string;
    max_results_per_keyword?: number;
    use_proxy?: boolean;
    rate_limit_rpm?: number;
  },
) =>
  transport.request(`/ops/public-sources/${encodeURIComponent(id)}`, anyBody, {
    method: 'PATCH',
    body: input,
  });
export const discoverPublicSources = (url: string) =>
  transport.request('/ops/public-sources/discovery', anyBody, {
    method: 'POST',
    body: { sites: [url] },
  });
export const reviewDiscoveredSource = (id: string, approve: boolean, reason: string) =>
  transport.request(`/ops/public-sources/${encodeURIComponent(id)}/review`, anyBody, {
    method: 'POST',
    body: { decision: approve ? 'approved' : 'rejected', reason },
  });

const conflictDecoder = object({
  content_id: str,
  source_name: str,
  revisions: arrayOf(
    object({
      revision_id: str,
      source_version: int,
      title: nullableStr,
      body: nullableStr,
      published_at: nullableStr,
    }),
  ),
});
export const fetchPoolConflicts = (signal?: AbortSignal) =>
  transport.request(
    '/ops/public-contents/conflicts',
    arrayOf(conflictDecoder),
    signal ? { signal } : {},
  );
export const resolvePoolConflict = (id: string, revisionId: string, reason: string) =>
  transport.request(`/ops/public-contents/${encodeURIComponent(id)}/resolve`, anyBody, {
    method: 'POST',
    body: { revision_id: revisionId, reason },
  });
